/**
 * Facebook Messenger Platform - Messages Webhook Types
 * 
 * These types represent the webhook event structure for messages events.
 * Triggered when a user sends a message to your page.
 * 
 * @see https://developers.facebook.com/docs/messenger-platform/reference/webhook-events/messages
 */

import {
  BaseWebhookEvent,
  WebhookEventType,
  WebhookPayload,
  BaseProcessingContext,
  extractBaseContext,
} from './base-types';

/**
 * Enumeration of attachment types supported by the Messenger Platform
 */
export enum AttachmentType {
  AUDIO = 'audio',
  FILE = 'file',
  IMAGE = 'image',
  VIDEO = 'video',
  FALLBACK = 'fallback',
  REEL = 'reel',
  IG_REEL = 'ig_reel',
}

/**
 * Enumeration of referral types for message referrals
 */
export enum ReferralType {
  OPEN_THREAD = 'OPEN_THREAD',
  PRODUCT = 'product',
  ADS = 'ads',
}

/**
 * Enumeration of referral sources
 */
export enum ReferralSource {
  MESSENGER_CODE = 'MESSENGER_CODE',
  DISCOVER_TAB = 'DISCOVER_TAB',
  ADS = 'ADS',
  SHORTLINK = 'SHORTLINK',
  CUSTOMER_CHAT_PLUGIN = 'CUSTOMER_CHAT_PLUGIN',
}

// Note: MessageSender and MessageRecipient are now replaced by
// WebhookSender and WebhookRecipient from base-types.ts
// Keeping these as deprecated aliases for backward compatibility

import { WebhookSender as MessageSender } from './base-types';
import { WebhookRecipient as MessageRecipient } from './base-types';
export { MessageSender, MessageRecipient };

/**
 * Represents a quick reply payload in a message
 */
export interface QuickReply {
  /** 
   * The payload string that was defined when the quick reply was sent.
   * Maximum 1000 characters.
   */
  payload: string;
}

/**
 * Represents the reply-to information when a message is a reply
 */
export interface ReplyTo {
  /** 
   * Message ID of the message being replied to 
   */
  mid: string;

  is_self_reply?: boolean;
}

/**
 * Base attachment payload interface
 */
export interface BaseAttachmentPayload {
  /** URL to the attachment file */
  url: string;
}

/**
 * Attachment payload for media files (audio, image, video, file)
 */
export interface MediaAttachmentPayload extends BaseAttachmentPayload {
  /** URL to the attachment file */
  url: string;
  
  /** Title of the attachment, if available */
  title?: string;
}

/**
 * Attachment payload for sticker attachments
 */
export interface StickerAttachmentPayload extends BaseAttachmentPayload {
  /** URL to the sticker image */
  url: string;
  
  /** Sticker ID for the sent sticker */
  sticker_id: number;
}

/**
 * Attachment payload for reel attachments (Instagram Reels, Facebook Reels)
 */
export interface ReelAttachmentPayload extends BaseAttachmentPayload {
  /** URL to the reel */
  url: string;
  
  /** Video ID of the reel */
  reel_video_id?: number;
}

/**
 * Attachment payload for fallback attachments
 */
export interface FallbackAttachmentPayload extends BaseAttachmentPayload {
  /** URL to the attachment */
  url: string;
  
  /** Title of the fallback attachment */
  title?: string;
}

/**
 * Union type for all possible attachment payload types
 */
export type AttachmentPayload = 
  | MediaAttachmentPayload 
  | StickerAttachmentPayload 
  | ReelAttachmentPayload 
  | FallbackAttachmentPayload;

/**
 * Represents an attachment in a message
 */
export interface MessageAttachment {
  /** The type of attachment */
  type: AttachmentType;
  
  /** The attachment payload containing the actual attachment data */
  payload: AttachmentPayload;
}

/**
 * Represents referral data in a message (for referral campaigns, ads, etc.)
 */
export interface MessageReferral {
  /** The source of the referral */
  source: ReferralSource;
  
  /** The type of referral */
  type: ReferralType;
  
  /** 
   * The optional ref parameter passed in the referral.
   * Maximum 250 characters.
   */
  ref?: string;
  
  /** 
   * URL of the website where the referral was triggered.
   * Only present for CUSTOMER_CHAT_PLUGIN source.
   */
  referer_uri?: string;
  
  /** 
   * Indicates whether the referral is from a guest user.
   * Only present for CUSTOMER_CHAT_PLUGIN source.
   */
  is_guest_user?: boolean;
  
  /** 
   * Product information for product referrals.
   * Only present when type is 'product'.
   */
  product?: {
    /** Product ID */
    id: string;
  };
  
  /** 
   * Ad information for ad referrals.
   * Only present when type is 'ads'.
   */
  ads?: {
    /** Ad ID */
    id: string;
    
    /** Ad title */
    title?: string;
    
    /** Ad image URL */
    image_url?: string;
  };
}

/**
 * Represents a command in a message (for bot commands)
 */
export interface MessageCommand {
  /** The name of the command */
  name: string;
}

/**
 * Represents the main message content in a webhook event
 */
export interface Message {
  /** 
   * Unique message identifier 
   */
  mid: string;

  is_echo?: boolean;
  app_id?: string | number;
  metadata?: string;
  
  /** 
   * Text content of the message.
   * Present for text messages and messages with quick replies.
   * Maximum 2000 UTF-8 characters.
   */
  text?: string;
  
  /** 
   * Quick reply payload, if the message was sent as a response to a quick reply.
   * Only present when the user taps a quick reply button.
   */
  quick_reply?: QuickReply;
  
  /** 
   * Reply-to information, if this message is a reply to another message.
   * Only present when the user replies to a specific message.
   */
  reply_to?: ReplyTo;
  
  /** 
   * Array of attachments sent with the message.
   * Can include images, audio, video, files, location, etc.
   */
  attachments?: MessageAttachment[];
  
  /** 
   * Referral information, if the message came from a referral.
   * Present when users click on ads, m.me links, etc.
   */
  referral?: MessageReferral;
  
  /** 
   * Array of commands, if the message contains bot commands.
   * Present when users send commands like /start, /help, etc.
   */
  commands?: MessageCommand[];
}

/**
 * Main webhook event structure for messages with discriminator
 * 
 * This event is triggered when a user sends a message to your page.
 * The webhook provides the message content and metadata.
 * 
 * @example Text message:
 * ```json
 * {
 *   "type": "message",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1458668856463,
 *   "message": {
 *     "mid": "mid.1458668856218:ed81099e15d3f4f233",
 *     "text": "Hello, world!"
 *   }
 * }
 * ```
 * 
 * @example Message with attachment:
 * ```json
 * {
 *   "type": "message",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1458668856463,
 *   "message": {
 *     "mid": "mid.1458668856218:ed81099e15d3f4f233",
 *     "attachments": [
 *       {
 *         "type": "image",
 *         "payload": {
 *           "url": "https://scontent.xx.fbcdn.net/v/image.jpg"
 *         }
 *       }
 *     ]
 *   }
 * }
 * ```
 * 
 * @example Message with quick reply:
 * ```json
 * {
 *   "type": "message",
 *   "sender": {
 *     "id": "1234567890123456"
 *   },
 *   "recipient": {
 *     "id": "9876543210987654"
 *   },
 *   "timestamp": 1458668856463,
 *   "message": {
 *     "mid": "mid.1458668856218:ed81099e15d3f4f233",
 *     "text": "Yes",
 *     "quick_reply": {
 *       "payload": "YES_PAYLOAD"
 *     }
 *   }
 * }
 * ```
 */
export interface MessageWebhookEvent extends BaseWebhookEvent {
  /** Discriminator for type narrowing */
  type: WebhookEventType.MESSAGE;
  
  /** The message content and metadata */
  message: Message;
}

/**
 * Complete webhook payload structure that includes the message event
 * along with other webhook metadata
 */
export interface MessageWebhookPayload extends WebhookPayload<MessageWebhookEvent> {}

/**
 * Type guard to check if a webhook event is a message event
 * 
 * @param event - The webhook event to check
 * @returns True if the event contains a message property
 * 
 * @example
 * ```typescript
 * if (isMessageEvent(event)) {
 *   // TypeScript now knows event has message property
 *   console.log(`Received message: ${event.message.text || '[attachment]'}`);
 * }
 * ```
 */
export function isMessageEvent(event: any): event is MessageWebhookEvent {
  return event && typeof event === 'object' && 'message' in event;
}

/**
 * Type guard to check if a message has text content
 * 
 * @param message - The message to check
 * @returns True if the message has text content
 */
export function isTextMessage(message: Message): message is Message & { text: string } {
  return typeof message.text === 'string' && message.text.length > 0;
}

/**
 * Type guard to check if a message has attachments
 * 
 * @param message - The message to check
 * @returns True if the message has attachments
 */
export function hasAttachments(message: Message): message is Message & { attachments: MessageAttachment[] } {
  return Array.isArray(message.attachments) && message.attachments.length > 0;
}

/**
 * Type guard to check if a message has a quick reply
 * 
 * @param message - The message to check
 * @returns True if the message has a quick reply
 */
export function hasQuickReply(message: Message): message is Message & { quick_reply: QuickReply } {
  return message.quick_reply !== undefined;
}

/**
 * Type guard to check if a message is a reply to another message
 * 
 * @param message - The message to check
 * @returns True if the message is a reply
 */
export function isReplyMessage(message: Message): message is Message & { reply_to: ReplyTo } {
  return message.reply_to !== undefined;
}

/**
 * Type guard to check if a message has referral data
 * 
 * @param message - The message to check
 * @returns True if the message has referral data
 */
export function hasReferral(message: Message): message is Message & { referral: MessageReferral } {
  return message.referral !== undefined;
}

/**
 * Type guard to check if an attachment is a specific type
 * 
 * @param attachment - The attachment to check
 * @param type - The attachment type to check for
 * @returns True if the attachment is of the specified type
 */
export function isAttachmentType<T extends AttachmentType>(
  attachment: MessageAttachment, 
  type: T
): attachment is MessageAttachment & { type: T } {
  return attachment.type === type;
}

/**
 * Utility type for extracting just the message data from a webhook event
 */
export type MessageData = Message;

/**
 * Utility type for common message properties that might be used in processing
 */
export interface MessageProcessingContext extends BaseProcessingContext {
  /** The message ID */
  messageId: string;
  
  /** Message text content, if available */
  text?: string;
  
  /** Whether the message has attachments */
  hasAttachments: boolean;
  
  /** Whether the message is a quick reply */
  isQuickReply: boolean;
  
  /** Whether the message is a reply to another message */
  isReply: boolean;
  
  /** Whether the message has referral data */
  hasReferral: boolean;
  
  /** Quick reply payload, if available */
  quickReplyPayload?: string;
  
  /** Replied message ID, if this is a reply */
  repliedToMessageId?: string;
}

/**
 * Helper function to extract processing context from a message event
 * 
 * @param event - The message webhook event
 * @returns Simplified processing context
 * 
 * @example
 * ```typescript
 * const context = extractMessageContext(webhookEvent);
 * console.log(`User ${context.senderId} sent: "${context.text || '[attachment]'}"`);
 * if (context.isQuickReply) {
 *   console.log(`Quick reply payload: ${context.quickReplyPayload}`);
 * }
 * ```
 */
export function extractMessageContext(event: MessageWebhookEvent): MessageProcessingContext {
  const { message } = event;
  const baseContext = extractBaseContext(event);
  
  return {
    ...baseContext,
    messageId: message.mid,
    text: message.text,
    hasAttachments: hasAttachments(message),
    isQuickReply: hasQuickReply(message),
    isReply: isReplyMessage(message),
    hasReferral: hasReferral(message),
    quickReplyPayload: message.quick_reply?.payload,
    repliedToMessageId: message.reply_to?.mid,
  };
}

/**
 * Helper function to get attachments of a specific type from a message
 * 
 * @param message - The message to extract attachments from
 * @param type - The attachment type to filter by
 * @returns Array of attachments of the specified type
 * 
 * @example
 * ```typescript
 * const images = getAttachmentsByType(message, AttachmentType.IMAGE);
 * console.log(`Message contains ${images.length} image(s)`);
 * ```
 */
export function getAttachmentsByType<T extends AttachmentType>(
  message: Message, 
  type: T
): Array<MessageAttachment & { type: T }> {
  if (!hasAttachments(message)) {
    return [];
  }
  
  return message.attachments.filter((attachment): attachment is MessageAttachment & { type: T } => 
    isAttachmentType(attachment, type)
  );
}

/**
 * Helper function to extract all URLs from message attachments
 * 
 * @param message - The message to extract URLs from
 * @returns Array of attachment URLs
 * 
 * @example
 * ```typescript
 * const urls = getAttachmentUrls(message);
 * console.log(`Message contains ${urls.length} attachment(s)`);
 * ```
 */
export function getAttachmentUrls(message: Message): string[] {
  if (!hasAttachments(message)) {
    return [];
  }
  
  return message.attachments.map(attachment => attachment.payload.url);
}

/**
 * Constants related to message limits and constraints
 */
export const MESSAGE_CONSTANTS = {
  /** Maximum length of message text */
  MAX_TEXT_LENGTH: 2000,
  
  /** Maximum length of quick reply payload */
  MAX_QUICK_REPLY_PAYLOAD_LENGTH: 1000,
  
  /** Maximum length of referral ref parameter */
  MAX_REFERRAL_REF_LENGTH: 250,
  
  /** Webhook event type identifier */
  EVENT_TYPE: 'message' as const,
} as const;

/**
 * Common attachment MIME types for validation
 */
export const ATTACHMENT_MIME_TYPES = {
  [AttachmentType.IMAGE]: [
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
  ],
  [AttachmentType.VIDEO]: [
    'video/mp4',
    'video/avi',
    'video/quicktime',
    'video/webm',
  ],
  [AttachmentType.AUDIO]: [
    'audio/mpeg',
    'audio/mp4',
    'audio/wav',
    'audio/ogg',
  ],
  [AttachmentType.FILE]: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain',
  ],
} as const;